import { useState, useEffect, useCallback } from "react";
import { API_BASE } from "@config/env";
import axios from "axios";
import { Modal, ModalFooter } from "@common/Modal";

// post() returns r.data directly (already parsed JSON)
const post = (ep, f) => {
  const fd = new FormData();
  Object.entries(f).forEach(([k,v]) => { if (v != null) fd.append(k, v); });
  return axios.post(`${API_BASE}${ep}`, fd).then(r => r.data).catch(() => null);
};

export default function RecommendModal({ resourceSlug, userSlug, firmId, candidateName, onClose }) {
  const [clientList,     setClientList]     = useState([]);
  const [selectedClients,setSelectedClients]= useState([]);  // array of user_slugs
  const [alreadySent,    setAlreadySent]    = useState([]);   // slugs already recommended
  const [subject,        setSubject]        = useState("");
  const [content,        setContent]        = useState("");
  const [errors,         setErrors]         = useState({});
  const [sending,        setSending]        = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [responseMsg,    setResponseMsg]    = useState(null);
  const [responseType,   setResponseType]   = useState(null);

  // Load clients + already-recommended slugs
  const loadClients = useCallback(async () => {
    setLoading(true);
    const [clientRes, recRes] = await Promise.all([
      post("get-clients-by-firm", { user_slug: userSlug, firm_id: firmId }),
      post("get-recommendation",  { resource_slug: resourceSlug }),
    ]);

    // Clients — response: { status:1, clients:[{client_name, user_slug}] }
    if (clientRes && Number(clientRes.status) === 1 && Array.isArray(clientRes.clients)) {
      setClientList(clientRes.clients.map((c, i) => ({
        id:   i + 1,
        name: c.client_name,
        slug: c.user_slug,
      })));
    } else {
      setClientList([]);
    }

    // Already recommended — response: { status:1, ids:[...slugs] }
    if (recRes && Number(recRes.status) === 1 && Array.isArray(recRes.ids)) {
      setAlreadySent(recRes.ids.map(s => s.trim()));
    } else {
      setAlreadySent([]);
    }

    setLoading(false);
  }, [userSlug, firmId, resourceSlug]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const toggleClient = (slug) => {
    setSelectedClients(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
    setErrors(e => ({ ...e, clients: "" }));
  };

  const getClientName = (slug) => {
    const c = clientList.find(c => c.slug === slug);
    return c ? c.name : slug;
  };

  const handleClose = () => {
    setErrors({}); setSubject(""); setContent(""); setSelectedClients([]);
    onClose();
  };

  const handleSend = async () => {
    const errs = {};
    if (selectedClients.length === 0) errs.clients = "Please select at least one client.";
    if (!subject.trim())              errs.subject = "Subject is required.";
    if (!content.trim())              errs.content = "Message content is required.";
    setErrors(errs);
    setResponseMsg(null);
    if (Object.keys(errs).length > 0) return;

    setSending(true);
    const res = await post("send-recommendation", {
      user_slug:     userSlug,
      resource_slug: resourceSlug,
      subject,
      content,
      clients: JSON.stringify(selectedClients),  // must be JSON stringified
    });
    setSending(false);

    if (res && Number(res.status) === 1) {
      setResponseType("success");
      setResponseMsg(res.message || "Recommendation sent successfully!");
      // Update already-sent list from server response
      if (Array.isArray(res.ids)) {
        setAlreadySent(res.ids.map(s => s.trim()));
      } else {
        setAlreadySent(prev => [...prev, ...selectedClients]);
      }
      setSelectedClients([]); setSubject(""); setContent(""); setErrors({});
      setTimeout(onClose, 1800);
    } else {
      setResponseType("error");
      setResponseMsg(res?.message || "Failed to send recommendation.");
    }
  };

  const INP = {
    width:"100%", boxSizing:"border-box", padding:"9px 12px",
    border:"1.5px solid #E5E7EB", borderRadius:9, fontSize:13,
    outline:"none", fontFamily:"inherit", color:"#111827",
  };

  const title = (
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ width:36, height:36, borderRadius:10, background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <i className="fa-solid fa-paper-plane" style={{ color:"#2563EB", fontSize:14 }} />
      </div>
      <div>
        <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)" }}>Send Recommendation</div>
        <div style={{ fontSize:12, color:"var(--fc-text-muted)" }}>{candidateName}</div>
      </div>
    </div>
  );

  return (
    <Modal title={title} onClose={!sending ? handleClose : undefined} maxWidth={520}
      footer={
        <ModalFooter
          onCancel={handleClose} cancelLabel="Close"
          onConfirm={handleSend}
          confirmLabel={sending
            ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:10}} /> Sending…</>
            : <><i className="fa-solid fa-paper-plane" style={{fontSize:10}} /> Send Recommendation</>}
          confirmDisabled={loading}
          confirmBusy={sending}
        />
      }>

      {/* Response message */}
      {responseMsg && (
        <div style={{ padding:"10px 14px", borderRadius:9, marginBottom:14, fontSize:13, fontWeight:600,
          background:responseType==="success"?"#F0FDF4":"#FEF2F2",
          color:responseType==="success"?"#16A34A":"#DC2626",
          border:`1px solid ${responseType==="success"?"#BBF7D0":"#FECACA"}` }}>
          <i className={`fa-solid ${responseType==="success"?"fa-circle-check":"fa-circle-exclamation"} me-2`} />
          {responseMsg}
        </div>
      )}

      {/* Selected clients preview */}
      <div style={{ minHeight:42, padding:"8px 12px", background:"#F9FAFB", border:"1px solid #E5E7EB", borderRadius:9, display:"flex", flexWrap:"wrap", gap:6, alignItems:"center", marginBottom:14 }}>
        {selectedClients.length === 0 ? (
          <span style={{ color:"#9CA3AF", fontSize:13, userSelect:"none" }}>Select clients from the list below</span>
        ) : selectedClients.map(slug => (
          <span key={slug} style={{ display:"flex", alignItems:"center", gap:5, padding:"3px 10px", background:"#2563EB", color:"#fff", borderRadius:99, fontSize:12, fontWeight:600 }}>
            {getClientName(slug)}
            <button onClick={()=>toggleClient(slug)} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.8)", padding:0, fontSize:11 }}>×</button>
          </span>
        ))}
      </div>

      {/* Client list */}
      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:8 }}>Select Client(s)</label>
        {loading ? (
          <div style={{ textAlign:"center", padding:"16px 0", color:"#9CA3AF", fontSize:13 }}>
            <i className="fa-solid fa-spinner fa-spin me-2" />Loading clients…
          </div>
        ) : clientList.length === 0 ? (
          <div style={{ padding:"10px 14px", background:"#F0F9FF", border:"1px solid #BAE6FD", borderRadius:9, fontSize:13, color:"#0369A1" }}>
            No clients found.
          </div>
        ) : (
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {clientList.map(client => {
              const isSelected = selectedClients.includes(client.slug);
              const wasSent    = alreadySent.includes(client.slug.toString());
              return (
                <div key={client.id}
                  style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 12px", border:`1px solid ${isSelected?"#2563EB":wasSent?"#BBF7D0":"#E5E7EB"}`, borderRadius:8, background:isSelected?"#EFF6FF":wasSent?"#F0FDF4":"#fff", cursor:wasSent?"default":"pointer", fontSize:13 }}
                  onClick={()=>!wasSent&&toggleClient(client.slug)}>
                  <input type="checkbox" checked={isSelected} disabled={wasSent} readOnly
                    style={{ accentColor:"#2563EB", width:14, height:14, pointerEvents:"none" }} />
                  <span style={{ color:wasSent?"#16A34A":"#374151", fontWeight:isSelected?600:400 }}>{client.name}</span>
                  {wasSent && <span style={{ fontSize:10, fontWeight:700, color:"#16A34A" }}>✓ Sent</span>}
                </div>
              );
            })}
          </div>
        )}
        {errors.clients && <div style={{ fontSize:12, color:"#DC2626", marginTop:6 }}>{errors.clients}</div>}
      </div>

      {/* Subject */}
      <div style={{ marginBottom:14 }}>
        <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>Subject</label>
        <input value={subject} onChange={e=>{setSubject(e.target.value);setErrors(x=>({...x,subject:""}));}}
          placeholder="Enter subject" style={{ ...INP, borderColor:errors.subject?"#DC2626":"#E5E7EB" }}
          onFocus={e=>e.target.style.borderColor="#2563EB"} onBlur={e=>e.target.style.borderColor=errors.subject?"#DC2626":"#E5E7EB"} />
        {errors.subject && <div style={{ fontSize:12, color:"#DC2626", marginTop:4 }}>{errors.subject}</div>}
      </div>

      {/* Content */}
      <div>
        <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>Message</label>
        <textarea value={content} onChange={e=>{setContent(e.target.value);setErrors(x=>({...x,content:""}));}}
          placeholder="Write your recommendation message…" rows={5}
          style={{ ...INP, resize:"vertical", borderColor:errors.content?"#DC2626":"#E5E7EB" }}
          onFocus={e=>e.target.style.borderColor="#2563EB"} onBlur={e=>e.target.style.borderColor=errors.content?"#DC2626":"#E5E7EB"} />
        {errors.content && <div style={{ fontSize:12, color:"#DC2626", marginTop:4 }}>{errors.content}</div>}
      </div>
    </Modal>
  );
}
